import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  Car,
  Check,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  GraduationCap,
  History,
  Loader2,
  Search,
  UserPlus,
} from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { useUiStore } from '../store/ui';
import PhoneLink from '../components/PhoneLink';
import { fmtDate, initials } from '../lib/dates';
import { CandidateSearchResult, Student } from '../types';

function StudentCard({ student }: { student: Student }) {
  const openBilan = useUiStore((s) => s.openBilan);
  const openLivret = useUiStore((s) => s.openLivret);
  const openEvaluation = useUiStore((s) => s.openEvaluation);
  const openLessons = useUiStore((s) => s.openLessons);
  const name = `${student.firstName} ${student.lastName}`;
  const pct = Math.min(
    100,
    Math.round((student.drivingHoursDone / student.drivingHoursTarget) * 100),
  );
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
      {/* En-tête cliquable : replie/déplie le détail de l'élève ; l'appel est à côté. */}
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex-1 min-w-0 text-left"
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
              {initials(student.firstName, student.lastName)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">{name}</span>
                {student.readyForExam && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 text-[11px] font-semibold">
                    <CheckCircle2 size={12} />
                    Prêt(e) pour l'examen
                  </span>
                )}
                {student.examDate && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[11px] font-semibold">
                    <GraduationCap size={12} />
                    Examen le {fmtDate(student.examDate)}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Permis B ·{' '}
                {student.vehicleType === 'automatic' ? 'Boîte automatique' : 'Boîte manuelle'}
              </div>
            </div>
            <ChevronDown
              size={18}
              className={`text-slate-400 flex-shrink-0 mt-1 transition-transform ${
                expanded ? 'rotate-180' : ''
              }`}
            />
          </div>

          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm text-slate-600">Heures de conduite</span>
            <span className="text-sm font-semibold text-slate-900">
              {student.drivingHoursDone}/{student.drivingHoursTarget}h
            </span>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full"
              style={{ width: `${pct}%` }}
            />
          </div>
        </button>
        <PhoneLink phone={student.phoneNumber} name={name} compact />
      </div>

      {expanded && (
        <div className="mt-4">
          <div className="space-y-1.5 mb-5">
            {student.phoneNumber && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <PhoneLink phone={student.phoneNumber} name={name} />
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Car size={14} className="text-slate-400 flex-shrink-0" />
              <span>
                {student.nextLessonAt
                  ? `Prochaine leçon : ${fmtDate(student.nextLessonAt)}`
                  : 'Aucune leçon planifiée'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <GraduationCap size={14} className="text-slate-400 flex-shrink-0" />
              <span>
                Examen :{' '}
                <span className="font-semibold text-slate-900">
                  {student.examDate ? fmtDate(student.examDate) : 'à planifier'}
                </span>
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => openBilan({ candidateId: student.id, candidateName: name })}
              className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors flex items-center justify-center gap-2"
            >
              <ClipboardCheck size={14} />
              Bilan de leçon
            </button>
            <button
              onClick={() => openLivret(student.id)}
              className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
            >
              <BookOpen size={14} />
              Voir le livret
            </button>
          </div>
          <button
            onClick={() => openLessons(student.id, name)}
            className="w-full mt-3 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            <History size={14} />
            Historique des leçons
          </button>
          <button
            onClick={() => openEvaluation(student.id)}
            className="w-full mt-3 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
          >
            <ClipboardList size={14} />
            Évaluation de départ
          </button>
        </div>
      )}
    </div>
  );
}

function AddStudentSearch() {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const term = q.trim();

  const { data: results, isFetching } = useQuery({
    queryKey: ['candidate-search', term],
    queryFn: () => instructorApi.searchCandidates(term),
    enabled: term.length >= 1,
  });

  const addMutation = useMutation({
    mutationFn: (id: string) => instructorApi.addStudent(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['students'] });
      void queryClient.invalidateQueries({ queryKey: ['candidate-search'] });
    },
    onError: (e: Error) => window.alert(e.message || 'Ajout impossible.'),
  });

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <label className="block text-sm font-semibold text-slate-800 mb-2">
        Ajouter un élève à votre liste
      </label>
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher un élève de l'auto-école (nom, e-mail)…"
          className="w-full pl-9 pr-3.5 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm"
        />
        {isFetching && (
          <Loader2
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin"
          />
        )}
      </div>

      {term.length >= 1 && (
        <div className="mt-3 divide-y divide-slate-100 max-h-72 overflow-y-auto">
          {results && results.length === 0 && !isFetching && (
            <p className="text-sm text-slate-500 py-3">Aucun élève trouvé.</p>
          )}
          {(results ?? []).map((c: CandidateSearchResult) => {
            const busy =
              addMutation.isPending && addMutation.variables === c.id;
            return (
              <div key={c.id} className="flex items-center gap-3 py-2.5">
                <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {initials(c.firstName, c.lastName)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {c.firstName} {c.lastName}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {c.email ?? 'Sans e-mail'} ·{' '}
                    {c.vehicleType === 'automatic'
                      ? 'Boîte auto'
                      : 'Boîte manuelle'}
                    {c.assignedToOther ? ' · rattaché à un autre moniteur' : ''}
                  </div>
                </div>
                {c.assignedToMe ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700">
                    <Check size={14} />
                    Dans votre liste
                  </span>
                ) : (
                  <button
                    onClick={() => addMutation.mutate(c.id)}
                    disabled={busy}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold transition-colors disabled:opacity-60"
                  >
                    {busy ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <UserPlus size={13} />
                    )}
                    {c.assignedToOther ? 'Reprendre' : 'Ajouter'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function StudentsView() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['students'],
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    queryFn: instructorApi.getStudents,
  });

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-slate-900">Mes élèves</h1>
        <p className="text-slate-600">
          {data ? `${data.length} élève(s) vous sont confiés.` : 'Suivi de vos élèves.'}
        </p>
      </div>

      <AddStudentSearch />

      {isLoading && <p className="text-slate-500">Chargement de vos élèves…</p>}
      {isError && (
        <p className="text-red-600">
          Impossible de charger vos élèves : {(error as Error).message}
        </p>
      )}

      {data && data.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          Aucun élève ne vous est rattaché pour le moment.
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {data.map((s) => (
            <StudentCard key={s.id} student={s} />
          ))}
        </div>
      )}
    </section>
  );
}
